import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COMPANY } from '../../mocks/siteData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'About', href: '/about' },
    { label: 'Service Areas', href: '/near-me' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/98 backdrop-blur-md border-b border-stone-100'
          : 'bg-transparent'
      }`}
    >
      <div className="px-6 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between h-22 py-3">

          {/* Logo — white logo on transparent PNG; use backing container for visibility */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <div
              className={`transition-all duration-300 rounded-xl px-3 py-1.5 ${
                scrolled
                  ? 'bg-stone-900'
                  : 'bg-white/10 backdrop-blur-sm'
              }`}
            >
              <img
                src={COMPANY.logo}
                alt="Bay City Remodeling & Bathrooms LLC"
                className="h-14 w-auto object-contain block"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                  scrolled ? 'text-stone-600 hover:text-stone-900' : 'text-white/75 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs — phone enlarged */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href={`tel:${COMPANY.phoneFull}`}
              className={`flex items-center gap-2.5 font-bold whitespace-nowrap transition-colors ${
                scrolled ? 'text-stone-800 hover:text-accent text-lg' : 'text-white/85 hover:text-white text-base'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-accent text-base" />
              </div>
              {COMPANY.phone}
            </a>
            <Link
              to="/about"
              className={`text-sm font-semibold px-6 py-3 rounded-lg transition-all whitespace-nowrap ${
                scrolled
                  ? 'bg-stone-950 hover:bg-stone-800 text-white'
                  : 'bg-accent hover:bg-accent-dark text-white'
              }`}
            >
              Free Consultation
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 transition-colors cursor-pointer ${scrolled ? 'text-stone-800' : 'text-white'}`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100">
          <div className="px-6 py-5 flex flex-col gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="py-3.5 text-stone-700 font-medium border-b border-stone-50 text-sm"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-5 flex flex-col gap-3">
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="flex items-center justify-center gap-2.5 py-4 border border-stone-200 rounded-lg text-stone-800 font-bold text-base"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-accent" />
                </div>
                {COMPANY.phone}
              </a>
              <Link
                to="/about"
                className="bg-stone-950 text-white text-center py-3.5 rounded-lg font-semibold text-sm whitespace-nowrap"
              >
                Request Free Consultation
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
